import {HoistModel, XH} from '@xh/hoist/core';
import {action, observable} from '@xh/hoist/mobx';
import {isFunction, isNil, isString} from 'lodash';
import {ReactNode} from 'react';

export type DraggableListIdSpec<T> = keyof T | ((data: T, index: number) => string | number);
export type DraggableListOnDropFn<T> = (params: DraggableListOnDropParams<T>) => void;
export type DraggableItemRenderer<T> = (params: {data: T; index: number; model: DraggableListModel<T>}) => ReactNode;
export type DragDirection = 'before' | 'after';

export interface DraggableListOnDropParams<T> {
    dragTarget: T;
    dragTargetIdx: number;
    dropTarget: T;
    dropTargetIdx: number;
    dragDirection: DragDirection;
    model: DraggableListModel<T>;
}

export class DraggableListModel<T> extends HoistModel {
    get data(): T[] {
        return this.componentProps.data;
    }

    get keyGen(): (data: T, index: number) => string | number {
        return this.parseIdSpec(this.componentProps.idSpec);
    }

    get onDrop(): DraggableListOnDropFn<T> {
        return this.componentProps.onDrop;
    }

    get renderer(): DraggableItemRenderer<T> {
        return this.componentProps.renderer;
    }

    @observable accessor dragTargetIdx: number = null;
    @observable accessor dropTargetIdx: number = null;

    get dragTarget(): T {
        return this.data[this.dragTargetIdx] ?? null;
    }

    get dropTarget(): T {
        return this.data[this.dropTargetIdx] ?? null;
    }

    get dragging(): boolean {
        return !isNil(this.dragTargetIdx);
    }

    get dragDirection(): DragDirection {
        return this.dragTargetIdx > this.dropTargetIdx ? 'before' : 'after';
    }

    @action onDragStartInternal(evt: DragEvent, dragTargetIdx: number) {
        evt.dataTransfer.setData(this.xhId, this.xhId);
        this.dragTargetIdx = dragTargetIdx;
    }

    @action onDragEndInternal(): void {
        this.dragTargetIdx = null;
    }

    @action onDragEnterInternal(evt: DragEvent, targetIdx: number) {
        if (!this.isValidDrop(evt, targetIdx)) return;
        evt.preventDefault();
        evt.dataTransfer.dropEffect = 'move';
        this.dropTargetIdx = targetIdx;
    }

    @action onDragLeaveInternal(evt: DragEvent, targetIdx: number) {
        // @ts-ignore
        if (evt.currentTarget.contains(evt.relatedTarget as Node)) return;

        if (this.dropTargetIdx === targetIdx) this.dropTargetIdx = null;
    }

    @action onDropInternal(evt: DragEvent, targetIdx: number) {
        if (!this.isValidDrop(evt, targetIdx)) return;
        try {
            this.onDrop({
                dragTarget: this.dragTarget,
                dragTargetIdx: this.dragTargetIdx,
                dropTarget: this.dropTarget,
                dropTargetIdx: this.dropTargetIdx,
                dragDirection: this.dragDirection,
                model: this
            });
        } finally {
            this.dragTargetIdx = null;
            this.dropTargetIdx = null;
        }
    }

    onDragOverInternal(evt: DragEvent, targetIdx: number) {
        if (this.isValidDrop(evt, targetIdx)) evt.preventDefault();
    }

    private isValidDrop(evt: DragEvent, targetIdx: number) {
        return evt.dataTransfer.types.includes(this.xhId) && targetIdx !== this.dragTargetIdx && !isNil(this.dragTargetIdx);
    }

    private parseIdSpec(idSpec): (data: T, index: number) => string | number {
        if (isString(idSpec)) return data => data[idSpec];
        if (isFunction(idSpec)) return (data, index) => idSpec(data, index);
        throw XH.exception('idSpec should be either a name of a property, or a function to generate and id.');
    }
}
